import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AdminTicketStatus, StatusCategory } from '@central/contracts';
import { Star } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import {
  alterarSituacao,
  apagarSituacao,
  chavesDeAdministracao,
  criarSituacao,
  listarSituacoes,
  reordenarSituacoes,
} from '@/features/admin/admin.api';
import {
  AcoesDaLinha,
  BotoesDeOrdem,
  Cabecalho,
  Campo,
  CascaDeCatalogo,
  Celula,
  Chave,
  chaveSugerida,
  CLASSE_DE_CAMPO,
  ConfirmacaoDeCatalogo,
  ContagemDeUso,
  EtiquetaDeAtividade,
  Linha,
  LinhaDeCabecalho,
  useEscritaDeCatalogo,
  type Intencao,
} from '@/features/admin/componentes';
import { Modal } from '@/features/tickets/Modal';

/**
 * Situações de chamado.
 *
 * A tela mais delicada das cinco, e a única com uma marca própria: a estrela da
 * **situação inicial**. Ela existe porque o tenant precisa de pelo menos uma
 * situação da categoria "Novo" ativa para que alguém consiga abrir chamado, e
 * até aqui esse fato só aparecia como um erro no momento errado — na abertura
 * do chamado, para outra pessoa, dizendo "Nenhum status inicial configurado
 * para o tenant". Mostrando qual é a linha, a decisão de desativá-la deixa de
 * ser cega.
 */

const CATEGORIAS: Array<[StatusCategory, string]> = [
  ['NEW', 'Novo'],
  ['IN_PROGRESS', 'Em andamento'],
  ['PENDING', 'Pendente'],
  ['RESOLVED', 'Resolvido'],
  ['CLOSED', 'Fechado'],
  ['CANCELLED', 'Cancelado'],
];

const ROTULO_DE_CATEGORIA = new Map(CATEGORIAS);

export function TicketStatusesPage() {
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.situacoes,
    queryFn: listarSituacoes,
  });
  const situacoes = consulta.data?.items ?? [];

  const [editando, setEditando] = useState<AdminTicketStatus | null>(null);
  const [criando, setCriando] = useState(false);
  const [confirmando, setConfirmando] = useState<{
    registro: AdminTicketStatus;
    intencao: Intencao;
  } | null>(null);

  const ordenacao = useEscritaDeCatalogo(reordenarSituacoes);
  const atividade = useEscritaDeCatalogo(
    ({ id, active, version }: { id: string; active: boolean; version: number }) =>
      alterarSituacao(id, { active, version }),
    () => setConfirmando(null),
  );
  const remocao = useEscritaDeCatalogo((id: string) => apagarSituacao(id), () =>
    setConfirmando(null),
  );

  const ocupado =
    ordenacao.mutacao.isPending || atividade.mutacao.isPending || remocao.mutacao.isPending;

  function mover(indice: number, direcao: -1 | 1) {
    const ids = situacoes.map((s) => s.id);
    const destino = indice + direcao;
    [ids[indice], ids[destino]] = [ids[destino] as string, ids[indice] as string];
    ordenacao.mutacao.mutate(ids);
  }

  function alternar(situacao: AdminTicketStatus) {
    // Reativar não tira nada de ninguém: vai direto. Desativar passa pela
    // confirmação, que é onde a contagem de chamados aparece.
    if (!situacao.active) {
      atividade.mutacao.mutate({ id: situacao.id, active: true, version: situacao.version });
      return;
    }
    setConfirmando({ registro: situacao, intencao: 'desativar' });
  }

  return (
    <>
      <CascaDeCatalogo
        titulo="Situações de chamado"
        resumo="Os estados por que um chamado passa. A ordem daqui é a ordem do seletor; a estrela marca a situação em que um chamado novo nasce."
        rotuloDoBotao="Nova situação"
        aoCriar={() => setCriando(true)}
        consulta={consulta}
        vazio="Nenhuma situação cadastrada. Sem ao menos uma da categoria “Novo”, ninguém consegue abrir chamado."
      >
        <LinhaDeCabecalho>
          <Cabecalho className="w-24">Ordem</Cabecalho>
          <Cabecalho>Situação</Cabecalho>
          <Cabecalho>Chave</Cabecalho>
          <Cabecalho>Categoria</Cabecalho>
          <Cabecalho>Relógio de SLA</Cabecalho>
          <Cabecalho>Em uso</Cabecalho>
          <Cabecalho>Estado</Cabecalho>
          <Cabecalho className="text-right">Ações</Cabecalho>
        </LinhaDeCabecalho>

        <tbody>
          {situacoes.map((situacao, indice) => (
            <Linha key={situacao.id} inativo={!situacao.active}>
              <Celula>
                <BotoesDeOrdem
                  primeira={indice === 0}
                  ultima={indice === situacoes.length - 1}
                  ocupado={ocupado}
                  aoSubir={() => mover(indice, -1)}
                  aoDescer={() => mover(indice, 1)}
                />
              </Celula>

              <Celula>
                <span className="flex items-center gap-2 font-medium">
                  {situacao.color ? (
                    <span
                      aria-hidden
                      className="h-2.5 w-2.5 flex-none rounded-full ring-1 ring-inset ring-border"
                      style={{ backgroundColor: situacao.color }}
                    />
                  ) : null}
                  {situacao.name}
                  {situacao.initial ? (
                    <span
                      title="Situação inicial: é a que um chamado novo recebe. O tenant não pode ficar sem uma."
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                    >
                      <Star className="h-3 w-3" aria-hidden />
                      inicial
                    </span>
                  ) : null}
                </span>
              </Celula>

              <Celula>
                <Chave valor={situacao.key} />
              </Celula>

              <Celula className="text-xs">
                {ROTULO_DE_CATEGORIA.get(situacao.category) ?? situacao.category}
              </Celula>

              <Celula className="text-xs">
                <span className={cn(situacao.slaClockRunning ? '' : 'text-warning')}>
                  {situacao.slaClockRunning ? 'corre' : 'pausado'}
                </span>
              </Celula>

              <Celula>
                <ContagemDeUso uso={situacao.usage} />
              </Celula>

              <Celula>
                <EtiquetaDeAtividade ativo={situacao.active} />
              </Celula>

              <Celula>
                <AcoesDaLinha
                  registro={situacao}
                  entidade="situação"
                  ocupado={ocupado}
                  aoEditar={() => setEditando(situacao)}
                  aoAlternarAtividade={() => alternar(situacao)}
                  aoRemover={() => setConfirmando({ registro: situacao, intencao: 'remover' })}
                />
              </Celula>
            </Linha>
          ))}
        </tbody>
      </CascaDeCatalogo>

      {ordenacao.erro ? (
        <p role="alert" className="text-sm text-destructive">
          {ordenacao.erro}
        </p>
      ) : null}

      <DialogoDeSituacao
        aberto={criando || editando !== null}
        situacao={editando}
        aoFechar={() => {
          setCriando(false);
          setEditando(null);
        }}
      />

      <ConfirmacaoDeCatalogo
        aberto={confirmando !== null}
        intencao={confirmando?.intencao ?? 'desativar'}
        entidade="situação"
        registro={confirmando?.registro ?? null}
        erro={atividade.erro ?? remocao.erro}
        ocupado={ocupado}
        aoFechar={() => setConfirmando(null)}
        aoConfirmar={() => {
          if (!confirmando) return;
          if (confirmando.intencao === 'remover') {
            remocao.mutacao.mutate(confirmando.registro.id);
            return;
          }
          atividade.mutacao.mutate({
            id: confirmando.registro.id,
            active: false,
            version: confirmando.registro.version,
          });
        }}
      />
    </>
  );
}

// ── criação e edição ───────────────────────────────────────────────────────

/**
 * O mesmo formulário para criar e para editar, com uma diferença: a chave.
 *
 * Na criação ela é um campo; na edição, um rótulo desabilitado com a
 * explicação do porquê. Esconder o campo na edição faria parecer que a chave
 * não existe; deixá-lo habilitado faria a pessoa digitar e receber 400. Mostrar
 * desabilitado, com o motivo, é o que responde a pergunta antes de ela ser
 * feita.
 */
function DialogoDeSituacao({
  aberto,
  situacao,
  aoFechar,
}: {
  aberto: boolean;
  situacao: AdminTicketStatus | null;
  aoFechar: () => void;
}) {
  const editando = situacao !== null;

  const [nome, setNome] = useState('');
  const [chave, setChave] = useState('');
  const [chaveTocada, setChaveTocada] = useState(false);
  const [categoria, setCategoria] = useState<StatusCategory>('NEW');
  const [relogio, setRelogio] = useState(true);
  const [peso, setPeso] = useState(0);
  const [semeado, setSemeado] = useState<string | null>(null);

  // Semeia os campos quando o diálogo abre para um registro diferente. Um
  // `useEffect` com dependência no objeto refaria isso a cada nova resposta da
  // listagem — e apagaria o que a pessoa acabou de digitar.
  const identidade = situacao?.id ?? (aberto ? 'novo' : null);
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setNome(situacao?.name ?? '');
    setChave(situacao?.key ?? '');
    setChaveTocada(situacao !== null);
    setCategoria(situacao?.category ?? 'NEW');
    setRelogio(situacao?.slaClockRunning ?? true);
    setPeso(situacao?.progressWeight ?? 0);
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCatalogo(async () => {
    if (situacao) {
      return alterarSituacao(situacao.id, {
        name: nome.trim(),
        category: categoria,
        slaClockRunning: relogio,
        progressWeight: peso,
        version: situacao.version,
      });
    }
    return criarSituacao({
      key: chave.trim(),
      name: nome.trim(),
      category: categoria,
      slaClockRunning: relogio,
      progressWeight: peso,
    });
  }, aoFechar);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={editando ? `Editar “${situacao.name}”` : 'Nova situação'}
      descricao={
        editando
          ? 'A chave não muda: integração, URL e automação a usam para achar este registro.'
          : 'A chave é definitiva. O nome pode ser corrigido depois.'
      }
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo id="situacao-nome" rotulo="Nome">
          <input
            id="situacao-nome"
            value={nome}
            onChange={(evento) => {
              setNome(evento.target.value);
              if (!chaveTocada) setChave(chaveSugerida(evento.target.value));
            }}
            required
            minLength={2}
            maxLength={80}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          id="situacao-chave"
          rotulo="Chave"
          dica={
            editando
              ? 'Imutável depois de criada.'
              : 'Minúsculas, dígitos, ponto e hífen. É por ela que automação e integração acham a situação.'
          }
        >
          <input
            id="situacao-chave"
            value={chave}
            onChange={(evento) => {
              setChaveTocada(true);
              setChave(evento.target.value);
            }}
            disabled={editando}
            required
            pattern="[a-z0-9][a-z0-9.\-]*[a-z0-9]"
            minLength={2}
            maxLength={60}
            className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            id="situacao-categoria"
            rotulo="Categoria"
            dica="É o que o motor lê. “Novo” marca a situação inicial."
          >
            <select
              id="situacao-categoria"
              value={categoria}
              onChange={(evento) => setCategoria(evento.target.value as StatusCategory)}
              className={CLASSE_DE_CAMPO}
            >
              {CATEGORIAS.map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            id="situacao-peso"
            rotulo="Peso no progresso"
            dica="Quanto um subchamado nesta situação soma ao progresso do pai (0 a 100)."
          >
            <input
              id="situacao-peso"
              type="number"
              min={0}
              max={100}
              value={peso}
              onChange={(evento) => setPeso(Number(evento.target.value))}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={relogio}
            onChange={(evento) => setRelogio(evento.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
          />
          <span>
            O relógio de SLA corre nesta situação
            <span className="block text-xs text-muted-foreground">
              Desligue em situações de espera — “aguardando usuário”, por exemplo.
            </span>
          </span>
        </label>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            {editando ? 'Salvar' : 'Criar situação'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
